import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    FormGroup,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Security as SecurityIcon,
    AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { adminApi, getAllPermissions } from '../../api/admin_api';
import PermissionModal from './PermissionModal';
import ManageAccountsModal from './ManageAccountsModal';
import './admin_user_management.css';
import { baseURL } from '../../config';

const AdminUserManagement = forwardRef((props, ref) => {
    // State management
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sourceFilter, setSourceFilter] = useState('all'); // F8: Source filter
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserForm, setShowUserForm] = useState(false);
    const [permissionModalOpen, setPermissionModalOpen] = useState(false);
    const [accountsModalOpen, setAccountsModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [showPermissionSection, setShowPermissionSection] = useState(false);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
        openCreateUserModal: () => {
            setShowUserForm(true);
            setIsEditMode(false);
            setSelectedPermissions([]);
            setShowPermissionSection(false);
        }
    }), []);

    // Fetch users and permissions on component mount
    useEffect(() => {
        fetchUsers();
        fetchPermissions();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No authentication token found');
                toast.error('No authentication token found');
                return;
            }

            // Make real API call to fetch users
            const response = await fetch(`${baseURL}/api/admin/users`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Handle direct array response (current API format)
            if (Array.isArray(data)) {
                const transformedUsers = data.map(user => {
                    return {
                        id: user.id || user.userId || `user-${Math.random().toString(36).substr(2, 9)}`, // Fallback ID if missing
                        email: user.email,
                        firstName: user.fullName?.split(' ')[0] || user.firstName || 'Unknown',
                        lastName: user.fullName?.split(' ').slice(1).join(' ') || user.lastName || 'User',
                        role: user.role, // Keep original role format
                        status: user.status,
                        createdAt: user.created_at,
                        permissions: user.permissions || [],
                        initialDeposit: user.initialDeposit || user.initial_deposit || 0 // Add initial deposit from API
                    };
                });

                setUsers(transformedUsers);
            }
            // Handle wrapped response format (if API changes)
            else if (data.success && data.data) {
                const transformedUsers = data.data.map(user => ({
                    id: user.id,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    role: user.role === 1 ? 'Admin' : 'Trader',
                    status: user.status,
                    createdAt: user.createdAt || user.created_at,
                    permissions: user.permissions || []
                }));

                setUsers(transformedUsers);
            } else {
                throw new Error(data.message || 'Failed to fetch users');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error(`Failed to fetch users: ${error.message}`);

            // Fallback to empty array if API fails
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPermissions = async () => {
        try {
            const response = await getAllPermissions();

            // Handle different response formats
            let permissions = [];
            if (Array.isArray(response.data)) {
                permissions = response.data;
            } else if (response.data) {
                permissions = response.data;
            }

            // Transform permissions to the format expected by the UI
            const transformedPermissions = permissions.map(p => ({
                id: p.permissionId || p.id,
                name: p.permissionName || p.name,
                category: p.category || 'General',
                description: p.description || ''
            }));

            setAvailablePermissions(transformedPermissions);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            toast.error('Failed to fetch permissions. Using default set.');

            // Fallback to hardcoded permissions if API fails
            setAvailablePermissions([
                { id: 1, name: 'Settings', category: 'Settings', description: 'Main settings access' },
                { id: 2, name: 'LP Management', category: 'Settings', description: 'Liquidity Provider Management' },
                { id: 3, name: 'Security', category: 'Settings', description: 'Security Settings' },
                { id: 4, name: 'Data Management', category: 'Settings', description: 'Data Management Settings' },
                { id: 5, name: 'Notification & Appearance', category: 'Settings', description: 'Notification and Appearance Settings' },
                { id: 6, name: 'Strategies', category: 'Settings', description: 'Trading Strategies' },
                { id: 33, name: 'Market Watch Section', category: 'Dashboard', description: 'Access to Market Watch Section' },
                { id: 34, name: 'Monitoring Section', category: 'Dashboard', description: 'Access to Monitoring Section' },
                { id: 35, name: 'Trade Panel', category: 'Dashboard', description: 'Access to Trade Panel' },
                { id: 38, name: 'Quick Order', category: 'Dashboard', description: 'Access to Quick Order' },
                { id: 39, name: 'Spread Order', category: 'Dashboard', description: 'Access to Spread Order' }
            ]);
        }
    };

    // Helper to determine user source (Platform or Q AI)
    const getUserSource = (user) => {
        // Check if user has Q AI-related fields (tokenBalance, qaiAccess, etc.)
        if (user.tokenBalance !== undefined || user.qaiAccess || user.source === 'qai') {
            return 'Q AI';
        }
        return 'Platform';
    };

    // Filter users based on search term and source filter (F8)
    const filteredUsers = users.filter(user => {
        const matchesSearch =
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName.toLowerCase().includes(searchTerm.toLowerCase());

        const userSource = getUserSource(user);
        const matchesSource = sourceFilter === 'all' ||
            (sourceFilter === 'platform' && userSource === 'Platform') ||
            (sourceFilter === 'qai' && userSource === 'Q AI');

        return matchesSearch && matchesSource;
    });

    // Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Menu actions
    const handleMenuOpen = (event, user) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUser(null);
    };

    const handleEdit = () => {
        if (selectedUser) {
            console.log('Edit user clicked. User:', selectedUser);

            // Map status from API format to form format
            // API: 1 = active, 0 = inactive (as per your specification)
            // Form: 'active', 'inactive'
            let mappedStatus = 'active';
            if (typeof selectedUser.status === 'number') {
                mappedStatus = selectedUser.status === 1 ? 'active' : 'inactive';
            } else if (typeof selectedUser.status === 'string') {
                mappedStatus = selectedUser.status.toLowerCase();
            }

            console.log('Original status:', selectedUser.status, '(type:', typeof selectedUser.status, ') -> Mapped status:', mappedStatus);

            setIsEditMode(true);
            setEditingUserId(selectedUser.id); // Save the user ID
            formik.setValues({
                email: selectedUser.email || '',
                firstName: selectedUser.firstName || '',
                lastName: selectedUser.lastName || '',
                password: '', // Don't populate password for edit
                role: selectedUser.role || 'Trader',
                status: mappedStatus
            });

            // Load user's permissions
            if (selectedUser.permissions && Array.isArray(selectedUser.permissions)) {
                setSelectedPermissions(selectedUser.permissions);
                setShowPermissionSection(true); // Show permission section if user has permissions
            } else {
                setSelectedPermissions([]);
            }

            setShowUserForm(true);
        }
        handleMenuClose();
    };

    const handleDeleteClick = () => {
        // Don't close menu yet to keep selectedUser
        setDeleteDialogOpen(true);
        setAnchorEl(null); // Close menu but keep selectedUser
    };

    const handleDeleteConfirm = async () => {
        console.log('Delete confirm clicked. Selected user:', selectedUser);

        if (!selectedUser) {
            console.error('No user selected for deletion');
            toast.error('No user selected for deletion');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error('No authentication token found');
                return;
            }

            console.log('Deleting user with ID:', selectedUser.id);
            console.log('Request URL:', `${baseURL}/api/admin/users/${selectedUser.id}`);

            const response = await fetch(`${baseURL}/api/admin/users/${selectedUser.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            console.log('Delete response status:', response.status);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Delete error response:', errorData);
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            toast.success('User deleted successfully!');
            setDeleteDialogOpen(false);
            setSelectedUser(null);
            fetchUsers(); // Refresh the user list
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(`Failed to delete user: ${error.message}`);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
    };

    const handleCreateUser = () => {
        setIsEditMode(false);
        formik.resetForm();
        setShowUserForm(true);
    };

    // Permission modal handlers
    const handleOpenPermissionModal = (user) => {
        // Safety check for user object
        if (!user) {
            console.error('Cannot open permission modal: user object is null/undefined');
            toast.error('Cannot open permission modal: no user selected');
            return;
        }

        if (!user.id && !user.userId) {
            console.error('Cannot open permission modal: user object missing ID field');
            toast.error('Cannot open permission modal: user ID not found');
            return;
        }

        setSelectedUser(user);
        setPermissionModalOpen(true);
    };

    const handleClosePermissionModal = () => {
        setPermissionModalOpen(false);
        setSelectedUser(null);
    };

    const handlePermissionUpdateSuccess = () => {
        // Refresh user list after permission update
        fetchUsers();
        // Success notification is already shown in PermissionModal
    };

    // Accounts modal handlers
    const handleOpenAccountsModal = (user) => {
        console.log('handleOpenAccountsModal called with user:', user);

        // Safety check for user object
        if (!user) {
            console.error('Cannot open accounts modal: user object is null/undefined');
            toast.error('Cannot open accounts modal: no user selected');
            return;
        }

        if (!user.id && !user.userId) {
            console.error('Cannot open accounts modal: user object missing ID field');
            toast.error('Cannot open accounts modal: user ID not found');
            return;
        }

        console.log('Setting selectedUser and opening modal for user:', user);
        setSelectedUser(user);
        setAccountsModalOpen(true);
    };

    const handleCloseAccountsModal = () => {
        setAccountsModalOpen(false);
        // Don't set selectedUser to null immediately - let the modal handle cleanup
        // The modal will be unmounted when accountsModalOpen becomes false
        setTimeout(() => {
            setSelectedUser(null);
        }, 100); // Small delay to ensure modal has finished using the user data
    };

    // Formik form for creating/editing users
    const formik = useFormik({
        initialValues: {
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            role: 'Trader',
            status: 'active'
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Invalid email address').required('Email is required'),
            firstName: Yup.string().required('First name is required'),
            lastName: Yup.string().required('Last name is required'),
            password: isEditMode
                ? Yup.string().min(8, 'Password must be at least 8 characters')
                : Yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
            role: Yup.string().required('Role is required'),
            status: Yup.string().required('Status is required')
        }),
        onSubmit: async (values) => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    toast.error('No authentication token found');
                    return;
                }

                // Role mapping (string to integer)
                const roleMapping = {
                    'Admin': 1,
                    'Trader': 0
                };

                // Status mapping (string to integer)
                // active = 1, inactive = 0 (as per API specification)
                const statusMapping = {
                    'active': 1,
                    'inactive': 0
                };

                if (isEditMode) {
                    console.log('Updating user. Edit mode:', isEditMode, 'User ID:', editingUserId);

                    if (!editingUserId) {
                        console.error('No user ID found for editing');
                        toast.error('Cannot update user: No user ID found');
                        return;
                    }

                    // Update user
                    const updateData = {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email,
                        role: roleMapping[values.role] || 0,
                        status: statusMapping[values.status] || 0
                    };

                    // Only include password if it's provided
                    if (values.password && values.password.trim() !== '') {
                        updateData.password = values.password;
                    }

                    console.log('Update data:', updateData);
                    console.log('Request URL:', `${baseURL}/api/admin/users/${editingUserId}`);

                    const response = await fetch(`${baseURL}/api/admin/users/${editingUserId}`, {
                        method: 'PUT',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(updateData)
                    });

                    console.log('Update response status:', response.status);

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        console.error('Update error response:', errorData);
                        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
                    }

                    toast.success('User updated successfully!');
                    setShowUserForm(false);
                    setIsEditMode(false);
                    setEditingUserId(null);
                    formik.resetForm();
                    setSelectedUser(null);
                    fetchUsers();
                } else {
                    // Create new user
                    const userData = {
                        Email: values.email,
                        FirstName: values.firstName,
                        LastName: values.lastName,
                        Password: values.password,
                        Role: roleMapping[values.role] || 0,
                        Status: values.status,
                        Permissions: selectedPermissions
                    };

                    const response = await adminApi.createUser(token, userData);

                    if (response.success) {
                        toast.success('User created successfully!');
                        setShowUserForm(false);
                        formik.resetForm();
                        setSelectedPermissions([]);
                        setShowPermissionSection(false);
                        fetchUsers();
                    } else {
                        toast.error(response.error || 'Failed to create user');
                    }
                }
            } catch (error) {
                console.error('Error saving user:', error);
                toast.error(`Failed to ${isEditMode ? 'update' : 'create'} user: ${error.message}`);
            }
        }
    });

    const handleCloseUserForm = () => {
        setShowUserForm(false);
        setIsEditMode(false);
        setEditingUserId(null);
        formik.resetForm();
        setSelectedPermissions([]);
        setShowPermissionSection(false);
        setSelectedUser(null);
    };

    return (
        <Box className="admin-user-management">
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 4,
                pb: 3,
                borderBottom: '2px solid rgba(124, 58, 237, 0.3)'
            }}>
                <Box>
                    <Typography variant="h4" sx={{
                        fontWeight: 800,
                        color: 'rgba(255, 255, 255, 1)',
                        fontSize: '1.875rem',
                        letterSpacing: '-0.02em',
                        mb: 0.5
                    }}>
                        User Management
                    </Typography>
                    <Typography sx={{
                        color: 'rgba(171, 171, 171, 1)',
                        fontSize: '0.875rem',
                        fontWeight: 500
                    }}>
                        Manage users, roles, and permissions
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={fetchUsers}
                        disabled={loading}
                        sx={{
                            color: '#7c3aed',
                            borderColor: '#e9d5ff',
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2.5,
                            py: 1,
                            '&:hover': {
                                borderColor: '#7c3aed',
                                backgroundColor: 'rgba(124, 58, 237, 0.05)'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={18} /> : 'Refresh'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateUser}
                        sx={{
                            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                            color: 'white',
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 14px 0 rgba(124, 58, 237, 0.35)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)',
                                boxShadow: '0 6px 20px 0 rgba(124, 58, 237, 0.45)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Add New User
                    </Button>
                </Box>
            </Box>

            {/* Search Bar and Filters */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <TextField
                    placeholder="Search users by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#9ca3af', fontSize: '1.25rem' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: 'white',
                            fontSize: '0.9375rem',
                            '& fieldset': {
                                borderColor: '#e5e7eb'
                            },
                            '&:hover fieldset': {
                                borderColor: '#d1d5db'
                            },
                            '&.Mui-focused': {
                                backgroundColor: 'white',
                                '& fieldset': {
                                    borderColor: '#7c3aed',
                                    borderWidth: '2px'
                                },
                                boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
                            }
                        }
                    }}
                />
                {/* F8: Source Filter */}
                <FormControl sx={{ minWidth: 150 }}>
                    <Select
                        value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value)}
                        displayEmpty
                        sx={{
                            borderRadius: 2,
                            backgroundColor: 'white',
                            fontSize: '0.9375rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e5e7eb'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#d1d5db'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7c3aed',
                                borderWidth: '2px'
                            }
                        }}
                    >
                        <MenuItem value="all">All Sources</MenuItem>
                        <MenuItem value="platform">Platform</MenuItem>
                        <MenuItem value="qai">Q AI</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Users Table */}
            <Paper sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                background: 'rgba(41, 43, 47, 1)'
            }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{
                                background: 'rgba(51, 53, 57, 1)',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: 'rgba(171, 171, 171, 1)',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    py: 1,
                                    px: 3
                                }}>User</TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: 'rgba(171, 171, 171, 1)',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    py: 1,
                                    px: 3
                                }}>Role</TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: 'rgba(171, 171, 171, 1)',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    py: 1,
                                    px: 3
                                }}>Status</TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: 'rgba(171, 171, 171, 1)',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    py: 1,
                                    px: 3
                                }}>Created</TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: 'rgba(171, 171, 171, 1)',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    py: 1,
                                    px: 3
                                }}>Source</TableCell>
                                <TableCell sx={{
                                    fontWeight: 700,
                                    color: 'rgba(171, 171, 171, 1)',
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    textAlign: 'center',
                                    py: 1,
                                    px: 3
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : filteredUsers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: 'rgba(171, 171, 171, 1)' }}>
                                        No users found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredUsers
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((user) => (
                                        <TableRow
                                            key={user.id}
                                            hover
                                            sx={{
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(51, 53, 57, 1)',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)'
                                                },
                                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                            }}
                                        >
                                            <TableCell sx={{ py: 0.75, px: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Box sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '8px',
                                                        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'white',
                                                        fontWeight: 700,
                                                        fontSize: '0.75rem',
                                                        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)'
                                                    }}>
                                                        {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{
                                                            fontWeight: 600,
                                                            color: 'rgba(255, 255, 255, 1)',
                                                            fontSize: '0.875rem',
                                                            lineHeight: 1.3,
                                                            mb: 0.2
                                                        }}>
                                                            {user.firstName} {user.lastName}
                                                        </Typography>
                                                        <Typography sx={{
                                                            color: 'rgba(171, 171, 171, 1)',
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1.2
                                                        }}>
                                                            {user.email}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75, px: 3 }}>
                                                <Chip
                                                    label={user.role}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: user.role === 'Admin' ? '#fef3c7' : '#dbeafe',
                                                        color: user.role === 'Admin' ? '#92400e' : '#1e40af',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        height: 24,
                                                        borderRadius: '6px',
                                                        '& .MuiChip-label': {
                                                            px: 1.5
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75, px: 3 }}>
                                                <Chip
                                                    label={user.status}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: user.status === 'active' ? '#d1fae5' : '#fee2e2',
                                                        color: user.status === 'active' ? '#065f46' : '#991b1b',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        height: 24,
                                                        borderRadius: '6px',
                                                        textTransform: 'capitalize',
                                                        '& .MuiChip-label': {
                                                            px: 1.5
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ py: 0.75, px: 3 }}>
                                                <Typography sx={{
                                                    color: 'rgba(171, 171, 171, 1)',
                                                    fontSize: '0.8125rem',
                                                    fontWeight: 500
                                                }}>
                                                    {new Date(user.createdAt).toLocaleDateString()}
                                                </Typography>
                                            </TableCell>
                                            {/* F7: Source Column */}
                                            <TableCell sx={{ py: 0.75, px: 3 }}>
                                                <Chip
                                                    label={getUserSource(user)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getUserSource(user) === 'Q AI' ? '#fae8ff' : '#e0e7ff',
                                                        color: getUserSource(user) === 'Q AI' ? '#86198f' : '#3730a3',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        height: 24,
                                                        borderRadius: '6px',
                                                        '& .MuiChip-label': {
                                                            px: 1.5
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', py: 0.75, px: 3 }}>
                                                <IconButton
                                                    onClick={(e) => handleMenuOpen(e, user)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'rgba(51, 53, 57, 1)',
                                                        borderRadius: '8px',
                                                        color: 'rgba(171, 171, 171, 1)',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(124, 58, 237, 0.1)',
                                                            color: '#7c3aed',
                                                            transform: 'scale(1.05)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <MoreVertIcon sx={{ fontSize: '1.25rem' }} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredUsers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'rgba(51, 53, 57, 1)',
                        color: 'rgba(255, 255, 255, 1)',
                        '& .MuiTablePagination-toolbar': {
                            color: 'rgba(255, 255, 255, 1)',
                            fontSize: '0.875rem'
                        },
                        '& .MuiTablePagination-selectLabel': {
                            color: 'rgba(255, 255, 255, 1)',
                            fontSize: '0.875rem',
                            fontWeight: 500
                        },
                        '& .MuiTablePagination-displayedRows': {
                            color: 'rgba(255, 255, 255, 1)',
                            fontSize: '0.875rem',
                            fontWeight: 500
                        },
                        '& .MuiTablePagination-select': {
                            color: 'rgba(255, 255, 255, 1)',
                            fontSize: '0.875rem',
                            fontWeight: 600
                        },
                        '& .MuiTablePagination-actions': {
                            color: 'rgba(171, 171, 171, 1)',
                            '& .MuiIconButton-root': {
                                color: 'rgba(171, 171, 171, 1)',
                                '&:hover': {
                                    backgroundColor: 'rgba(124, 58, 237, 0.1)',
                                    color: '#7c3aed'
                                },
                                '&.Mui-disabled': {
                                    color: 'rgba(255, 255, 255, 0.3)'
                                }
                            }
                        }
                    }}
                />
            </Paper>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 10px 25px 0 rgba(0, 0, 0, 0.35)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        background: 'rgba(41, 43, 47, 1)'
                    }
                }}
            >
                <MenuItem onClick={handleEdit} sx={{ py: 1.5, color: 'rgba(255, 255, 255, 1)', '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.1)' } }}>
                    <EditIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(171, 171, 171, 1)' }} />
                    Edit User
                </MenuItem>
                <MenuItem onClick={() => handleOpenPermissionModal(selectedUser)} sx={{ py: 1.5, color: 'rgba(255, 255, 255, 1)', '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.1)' } }}>
                    <SecurityIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(171, 171, 171, 1)' }} />
                    Manage Permissions
                </MenuItem>
                <MenuItem onClick={() => {
                    const userToManage = selectedUser; // Capture user before menu closes
                    handleMenuClose();
                    handleOpenAccountsModal(userToManage);
                }} sx={{ py: 1.5, color: 'rgba(255, 255, 255, 1)', '&:hover': { backgroundColor: 'rgba(124, 58, 237, 0.1)' } }}>
                    <AccountBalanceIcon sx={{ mr: 1.5, fontSize: 20, color: 'rgba(171, 171, 171, 1)' }} />
                    Manage Accounts
                </MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ py: 1.5, color: '#dc2626' }}>
                    <DeleteIcon sx={{ mr: 1.5, fontSize: 20 }} />
                    Delete User
                </MenuItem>
            </Menu>

            {/* Add New User Dialog */}
            <Dialog 
                open={showUserForm} 
                onClose={handleCloseUserForm}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 25px 50px 0 rgba(0, 0, 0, 0.35)',
                        backgroundColor: 'rgba(41, 43, 47, 1)',
                        color: 'rgba(255, 255, 255, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }
                }}
                className="admin-user-form-dialog"
            >
                <DialogTitle sx={{
                    pb: 2,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                    color: 'rgba(255, 255, 255, 1)'
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>
                        {isEditMode ? 'Edit User' : 'Add New User'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 1 }}>
                        {isEditMode
                            ? 'Update user information, role, and status'
                            : 'Create a new user account with role and permissions'
                        }
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 3, pb: 3, backgroundColor: 'rgba(41, 43, 47, 1)' }}>
                <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2, mt: 2 }}>
                                <TextField
                                    fullWidth
                                    label="First Name"
                                    name="firstName"
                                    value={formik.values.firstName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: 'rgba(255, 255, 255, 1)',
                                        backgroundColor: 'rgba(51, 53, 57, 1)',
                                        '& fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(124, 58, 237, 0.5)'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7c3aed'
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgba(171, 171, 171, 1)'
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#7c3aed'
                                    }
                                }}
                                />
                                <TextField
                                    fullWidth
                                    label="Last Name"
                                    name="lastName"
                                    value={formik.values.lastName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    variant="outlined"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: 'rgba(255, 255, 255, 1)',
                                        backgroundColor: 'rgba(51, 53, 57, 1)',
                                        '& fieldset': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)'
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgba(124, 58, 237, 0.5)'
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#7c3aed'
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: 'rgba(171, 171, 171, 1)'
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': {
                                        color: '#7c3aed'
                                    }
                                }}
                                />
                            </Box>

                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                            variant="outlined"
                            sx={{ mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: 'rgba(255, 255, 255, 1)',
                                    backgroundColor: 'rgba(51, 53, 57, 1)',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(124, 58, 237, 0.5)'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#7c3aed'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgba(171, 171, 171, 1)'
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#7c3aed'
                                }
                            }}
                        />
                        
                            <TextField
                                fullWidth
                                label={isEditMode ? "Password (leave blank to keep current)" : "Password"}
                                name="password"
                                type="password"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                variant="outlined"
                                placeholder={isEditMode ? "Leave blank to keep current password" : ""}
                            sx={{ mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: 'rgba(255, 255, 255, 1)',
                                    backgroundColor: 'rgba(51, 53, 57, 1)',
                                    '& fieldset': {
                                        borderColor: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(124, 58, 237, 0.5)'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#7c3aed'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: 'rgba(171, 171, 171, 1)'
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#7c3aed'
                                }
                            }}
                        />
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                                <FormControl fullWidth>
                                <InputLabel sx={{ color: 'rgba(171, 171, 171, 1)', '&.Mui-focused': { color: '#7c3aed' } }}>Role</InputLabel>
                                    <Select
                                        name="role"
                                        value={formik.values.role}
                                        onChange={formik.handleChange}
                                        label="Role"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 1)',
                                        backgroundColor: 'rgba(51, 53, 57, 1)',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(124, 58, 237, 0.5)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                    >
                                        <MenuItem value="Admin">Admin</MenuItem>
                                        <MenuItem value="Trader">Trader</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth>
                                <InputLabel sx={{ color: 'rgba(171, 171, 171, 1)', '&.Mui-focused': { color: '#7c3aed' } }}>Status</InputLabel>
                                    <Select
                                        name="status"
                                        value={formik.values.status}
                                        onChange={formik.handleChange}
                                        label="Status"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 1)',
                                        backgroundColor: 'rgba(51, 53, 57, 1)',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(255, 255, 255, 0.1)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(124, 58, 237, 0.5)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                        {/* Permission Assignment Section - Only show for Trader role in create mode */}
                        {!isEditMode && formik.values.role === 'Trader' && (
                            <Box sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: 'rgba(51, 53, 57, 1)',
                                borderRadius: 2,
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 1)' }}>
                                    Assign Permissions (Trader Role)
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setShowPermissionSection(!showPermissionSection)}
                                    sx={{
                                        color: '#7c3aed',
                                        borderColor: '#7c3aed',
                                        '&:hover': {
                                            borderColor: '#9F7AEA',
                                            backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                        }
                                    }}
                                >
                                    {showPermissionSection ? 'Hide Permissions' : 'Assign Permissions'}
                                </Button>
                            </Box>
                            
                            {showPermissionSection && (
                                <Box sx={{ mt: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="body2" sx={{ color: 'rgba(171, 171, 171, 1)' }}>
                                            Select permissions to assign to this user. You can also manage permissions after creating the user.
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    const allPermissionIds = availablePermissions.map(p => p.id);
                                                    setSelectedPermissions(allPermissionIds);
                                                }}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    py: 0.5,
                                                    color: '#7c3aed',
                                                    borderColor: '#7c3aed',
                                                    '&:hover': {
                                                        borderColor: '#9F7AEA',
                                                        backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                                    }
                                                }}
                                            >
                                                Select All
                                            </Button>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setSelectedPermissions([])}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    py: 0.5,
                                                    color: 'rgba(171, 171, 171, 1)',
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                    '&:hover': {
                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                    }
                                                }}
                                            >
                                                Clear All
                                            </Button>
                                        </Box>
                                    </Box>
                                    
                                    {/* Show the comprehensive permissions structure grouped by category */}
                                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                                        {Object.entries(
                                            availablePermissions.reduce((acc, permission) => {
                                                if (!acc[permission.category]) {
                                                    acc[permission.category] = [];
                                                }
                                                acc[permission.category].push(permission);
                                                return acc;
                                            }, {})
                                        ).map(([category, permissions]) => (
                                            <Box key={category} sx={{ mb: 3 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 1, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                                    <Typography variant="subtitle2" sx={{
                                                        fontWeight: 600,
                                                        color: 'rgba(255, 255, 255, 1)'
                                                    }}>
                                                        {category} ({permissions.length} permissions)
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => {
                                                                const categoryPermissionIds = permissions.map(p => p.id);
                                                                const newSelected = [...new Set([...selectedPermissions, ...categoryPermissionIds])];
                                                                setSelectedPermissions(newSelected);
                                                            }}
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                py: 0.25,
                                                                px: 1,
                                                                minWidth: 'auto',
                                                                color: '#7c3aed',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                                                                }
                                                            }}
                                                        >
                                                            All
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() => {
                                                                const categoryPermissionIds = permissions.map(p => p.id);
                                                                const newSelected = selectedPermissions.filter(id => !categoryPermissionIds.includes(id));
                                                                setSelectedPermissions(newSelected);
                                                            }}
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                py: 0.25,
                                                                px: 1,
                                                                minWidth: 'auto',
                                                                color: 'rgba(171, 171, 171, 1)',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                                }
                                                            }}
                                                        >
                                                            None
                                                        </Button>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ pl: 2 }}>
                                                    {permissions.map((permission) => (
                                <FormControlLabel
                                                            key={permission.id}
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedPermissions.includes(permission.id)}
                                                                    onChange={(e) => {
                                                                        if (e.target.checked) {
                                                                            setSelectedPermissions([...selectedPermissions, permission.id]);
                                                                        } else {
                                                                            setSelectedPermissions(selectedPermissions.filter(id => id !== permission.id));
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        color: 'rgba(171, 171, 171, 1)',
                                                                        '&.Mui-checked': {
                                                                            color: '#7C3AED'
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'rgba(255, 255, 255, 1)' }}>
                                                                        {permission.name}
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)' }}>
                                                                        {permission.description}
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            sx={{ display: 'block', mb: 0.5 }}
                                                        />
                                                    ))}
                            </Box>
                        </Box>
                                        ))}
                                    </Box>

                                    <Typography variant="caption" sx={{ color: 'rgba(171, 171, 171, 1)', mt: 1, display: 'block' }}>
                                        {selectedPermissions.length} permissions selected
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        )}
                    </form>
                    </DialogContent>
                <DialogActions sx={{ p: 3, backgroundColor: 'rgba(51, 53, 57, 1)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Button
                        onClick={handleCloseUserForm}
                        sx={{
                            color: 'rgba(171, 171, 171, 1)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)'
                            }
                        }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="contained" 
                        onClick={formik.handleSubmit}
                        disabled={formik.isSubmitting}
                        sx={{ 
                                background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                                color: 'white',
                            px: 3,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)'
                            }
                            }}
                        >
                        {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : isEditMode ? 'Update User' : 'Create User'}
                        </Button>
                    </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 25px 50px 0 rgba(0, 0, 0, 0.35)',
                        backgroundColor: 'rgba(41, 43, 47, 1)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                    }
                }}
            >
                <DialogTitle sx={{ pb: 2, fontWeight: 600, color: 'rgba(255, 255, 255, 1)', background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' }}>
                    Delete User
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: 'rgba(41, 43, 47, 1)' }}>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 1)' }}>
                        Are you sure you want to delete <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: 'rgba(51, 53, 57, 1)', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <Button onClick={handleDeleteCancel} sx={{ color: 'rgba(171, 171, 171, 1)' }}>Cancel</Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Permission Modal */}
            {permissionModalOpen && (
                <PermissionModal
                    open={permissionModalOpen}
                    onClose={handleClosePermissionModal}
                    user={selectedUser}
                    onSuccess={handlePermissionUpdateSuccess}
                />
            )}

            {/* Manage Accounts Modal */}
            {accountsModalOpen && (
                <>
                    {console.log('Rendering ManageAccountsModal with selectedUser:', selectedUser)}
                    <ManageAccountsModal
                        open={accountsModalOpen}
                        onClose={handleCloseAccountsModal}
                        user={selectedUser}
                    />
                </>
            )}
        </Box>
    );
});

AdminUserManagement.displayName = 'AdminUserManagement';

export default AdminUserManagement;